import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { LANGUAGES, useI18n, type Lang } from '../../i18n/index.tsx';
import { Flag } from '../ui/Flag.tsx';
import { Icon } from '../ui/Icon.tsx';

const ENTRIES = Object.entries(LANGUAGES) as [Lang, string][];

/**
 * Language picker in the platform's colours (a native <select> renders its list with the OS palette).
 * A listbox driven from its button: arrows move, Enter/Space choose, Escape or Tab close.
 */
export function LangMenu() {
  const { lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const current = Math.max(0, ENTRIES.findIndex(([k]) => k === lang));

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [open]);

  const show = () => {
    setFocus(current);
    setOpen(true);
  };
  const choose = (k: Lang) => {
    setLang(k);
    setOpen(false);
  };
  const onKey = (e: KeyboardEvent) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      show();
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocus((f) => (f + 1) % ENTRIES.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocus((f) => (f - 1 + ENTRIES.length) % ENTRIES.length);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      choose(ENTRIES[focus][0]);
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <div className="lang-menu" ref={ref}>
      <button
        type="button"
        className="lang-select"
        aria-label="language"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-activedescendant={open ? `lang-opt-${ENTRIES[focus][0]}` : undefined}
        onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={onKey}
      >
        <Flag lang={lang} />
        <span>{lang.toUpperCase()}</span>
        <Icon name="chevron" className={open ? 'flip' : undefined} />
      </button>
      {open ? (
        <ul className="lang-list" role="listbox" aria-label="language">
          {ENTRIES.map(([k, name], i) => (
            <li
              key={k}
              id={`lang-opt-${k}`}
              role="option"
              aria-selected={k === lang}
              className={i === focus ? 'focus' : undefined}
              onMouseEnter={() => setFocus(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(k)}
            >
              <Flag lang={k} width={26} />
              <b>{k.toUpperCase()}</b>
              <span>{name}</span>
              {k === lang ? <Icon name="check" /> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
